import { env } from '@/lib/env';
import { getActiveWorkspaceId } from './workspace-scope';

export interface ApiErrorDetail {
  path?: string;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation messages keyed by path, for wiring into forms. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'VALIDATION_ERROR' || !Array.isArray(this.details)) return {};
    return Object.fromEntries(
      (this.details as ApiErrorDetail[]).filter((d) => d.path).map((d) => [d.path!, d.message]),
    );
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /**
   * Platform console: act on this client workspace for one request (super admins only — the server
   * ignores it for everyone else). Defaults to the workspace currently open.
   */
  workspaceId?: string;
}

let unauthorizedHandler: (() => void) | null = null;
/** Called when the session can't be refreshed — the app redirects to /login. */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedHandler = handler;
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null;
  };
}

const NO_REFRESH = new Set(['/auth/login', '/auth/signup', '/auth/refresh', '/auth/logout']);

let refreshInFlight: Promise<boolean> | null = null;
/** Single-flight: concurrent 401s (and the realtime socket) share one refresh request. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= fetch(`${env.apiUrl}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${env.apiUrl}/api/v1${path}`);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  return url.toString();
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const workspaceId = opts.workspaceId ?? getActiveWorkspaceId();
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
}

/** Typed fetch wrapper for the support API (cookie auth, transparent token refresh, standard errors). */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res: Response;
  try {
    res = await send(path, opts);
    if (res.status === 401 && !NO_REFRESH.has(path)) {
      if (await refreshSession()) res = await send(path, opts);
      if (res.status === 401) unauthorizedHandler?.();
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the support server. Check your connection.');
  }

  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string; details?: unknown };
  } | null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      json?.error?.code ?? 'HTTP_ERROR',
      json?.error?.message ?? res.statusText ?? 'Request failed',
      json?.error?.details,
      res.headers.get('x-request-id'),
    );
  }
  return json as T;
}
